import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../axios/axiosInstance";
import { getApiErrorMessage, toastApiError } from "../../helper/api/toastApiError";

export const GUMRUK_AGC_STORAGE_KEY = "gumrukAgcPendingOperation";

const readPendingOperation = () => {
  if (typeof window === "undefined") return null;

  try {
    const value = sessionStorage.getItem(GUMRUK_AGC_STORAGE_KEY);
    if (!value) return null;

    const parsed = JSON.parse(value);
    if (!parsed?.operationId) return null;

    return {
      operationId: parsed.operationId,
      shipmentId: parsed.shipmentId ?? null,
      assignmentContext: parsed.assignmentContext ?? null,
    };
  } catch {
    sessionStorage.removeItem(GUMRUK_AGC_STORAGE_KEY);
    return null;
  }
};

const getResponseData = (responseBody) => responseBody?.data ?? responseBody ?? {};

const rejectApiError = (errorOrBody, thunkApi, fallback) => {
  const message = toastApiError(errorOrBody, fallback);
  return thunkApi.rejectWithValue({ message });
};

export const prepareGumrukAgcApproval = createAsyncThunk(
  "gumrukAgc/prepareApproval",
  async (operationId, thunkApi) => {
    try {
      const { data } = await axiosInstance.post("/GumrukAgc/PrepareEntryApproval", {
        operationId,
      });

      if (data?.statusCode && data.statusCode !== 200) {
        return rejectApiError(data, thunkApi, "Gümrük giriş bildirimi hazırlanamadı.");
      }

      return getResponseData(data);
    } catch (error) {
      return rejectApiError(error, thunkApi, "Gümrük giriş bildirimi hazırlanamadı.");
    }
  }
);

export const getGumrukAgcCaptcha = createAsyncThunk(
  "gumrukAgc/getCaptcha",
  async (challengeId, thunkApi) => {
    try {
      const { data } = await axiosInstance.get("/GumrukAgc/GetCaptcha", {
        params: {
          challengeId,
          _: Date.now(),
        },
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
      });

      return { challengeId, blob: data };
    } catch (error) {
      return rejectApiError(error, thunkApi, "CAPTCHA görseli alınamadı.");
    }
  }
);

export const approveGumrukAgcEntry = createAsyncThunk(
  "gumrukAgc/approveEntry",
  async ({ operationId, challengeId, captchaAnswer }, thunkApi) => {
    try {
      const { data } = await axiosInstance.post("/GumrukAgc/ApproveEntryNotification", {
        operationId,
        challengeId: challengeId || null,
        captchaAnswer: captchaAnswer || null,
      });

      if (data?.statusCode && data.statusCode !== 200) {
        return rejectApiError(data, thunkApi, "Gümrük giriş bildirimi onaylanamadı.");
      }

      return getResponseData(data);
    } catch (error) {
      return rejectApiError(error, thunkApi, "Gümrük giriş bildirimi onaylanamadı.");
    }
  }
);

export const rejectGumrukAgcEntry = createAsyncThunk(
  "gumrukAgc/rejectEntry",
  async (operationId, thunkApi) => {
    try {
      const { data } = await axiosInstance.post("/GumrukAgc/RejectEntryNotification", {
        operationId,
      });

      if (data?.statusCode && data.statusCode !== 200) {
        return rejectApiError(data, thunkApi, "Gümrük giriş bildirimi reddedilemedi.");
      }

      return getResponseData(data);
    } catch (error) {
      return rejectApiError(error, thunkApi, "Gümrük giriş bildirimi reddedilemedi.");
    }
  }
);

export const getGumrukAgcEntryStatus = createAsyncThunk(
  "gumrukAgc/getEntryStatus",
  async (operationId, thunkApi) => {
    try {
      const { data } = await axiosInstance.get("/GumrukAgc/GetEntryStatus", {
        params: { operationId },
      });

      if (data?.statusCode && data.statusCode !== 200) {
        return rejectApiError(data, thunkApi, "Gümrük işlem durumu alınamadı.");
      }

      return getResponseData(data);
    } catch (error) {
      return rejectApiError(error, thunkApi, "Gümrük işlem durumu alınamadı.");
    }
  }
);

export const approveGumrukAgcRegistrationWarning = createAsyncThunk(
  "gumrukAgc/approveRegistrationWarning",
  async (operationId, thunkApi) => {
    try {
      const { data } = await axiosInstance.post("/GumrukAgc/ApproveRegistrationWarning", {
        operationId,
      });

      if (data?.statusCode && data.statusCode !== 200) {
        return rejectApiError(data, thunkApi, "GTB tescil uyarısı onaylanamadı.");
      }

      return getResponseData(data);
    } catch (error) {
      return rejectApiError(error, thunkApi, "GTB tescil uyarısı onaylanamadı.");
    }
  }
);

const storedOperation = readPendingOperation();

const initialState = {
  operationId: storedOperation?.operationId ?? null,
  shipmentId: storedOperation?.shipmentId ?? null,
  assignmentContext: storedOperation?.assignmentContext ?? null,
  operation: null,
  captchaRequired: false,
  sessionAvailable: false,
  challengeId: null,
  challengeExpiresAt: null,
  isPreparing: false,
  isLoadingCaptcha: false,
  isApproving: false,
  isRejecting: false,
  isCheckingStatus: false,
  isApprovingWarning: false,
  errorMessage: null,
};

const getOperation = (payload) => payload?.operation ?? payload?.Operation ?? null;

const gumrukAgcSlice = createSlice({
  name: "gumrukAgc",
  initialState,
  reducers: {
    setPendingGumrukAgcOperation: (state, action) => {
      state.operationId = action.payload.operationId;
      state.shipmentId = action.payload.shipmentId ?? null;
      state.assignmentContext = action.payload.assignmentContext ?? null;
      state.operation = null;
      state.captchaRequired = false;
      state.sessionAvailable = false;
      state.challengeId = null;
      state.challengeExpiresAt = null;
      state.errorMessage = null;
    },
    clearGumrukAgcOperation: () => ({
      ...initialState,
      operationId: null,
      shipmentId: null,
      assignmentContext: null,
    }),
    setGumrukAgcOperationStatus: (state, action) => {
      state.operation = {
        ...(state.operation ?? {}),
        status: action.payload,
      };
    },
    clearGumrukAgcError: (state) => {
      state.errorMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(prepareGumrukAgcApproval.pending, (state) => {
        state.isPreparing = true;
        state.errorMessage = null;
        state.captchaRequired = false;
        state.sessionAvailable = false;
        state.challengeId = null;
        state.challengeExpiresAt = null;
      })
      .addCase(prepareGumrukAgcApproval.fulfilled, (state, action) => {
        state.isPreparing = false;
        state.operation = getOperation(action.payload) ?? state.operation;
        state.operationId = state.operation?.operationId ?? state.operationId;
        state.shipmentId = state.operation?.shipmentId ?? state.shipmentId;
        state.captchaRequired = Boolean(action.payload?.captchaRequired);
        state.sessionAvailable = Boolean(action.payload?.sessionAvailable);
        state.challengeId = action.payload?.challengeId ?? null;
        state.challengeExpiresAt = action.payload?.captchaRequired
          ? Date.now() + 180000
          : null;
        state.errorMessage = null;
      })
      .addCase(prepareGumrukAgcApproval.rejected, (state, action) => {
        state.isPreparing = false;
        state.errorMessage = action.payload?.message || "Gümrük giriş bildirimi hazırlanamadı.";
      })
      .addCase(getGumrukAgcCaptcha.pending, (state) => {
        state.isLoadingCaptcha = true;
        state.errorMessage = null;
      })
      .addCase(getGumrukAgcCaptcha.fulfilled, (state) => {
        state.isLoadingCaptcha = false;
      })
      .addCase(getGumrukAgcCaptcha.rejected, (state, action) => {
        state.isLoadingCaptcha = false;
        state.errorMessage = action.payload?.message || "CAPTCHA görseli alınamadı.";
      })
      .addCase(approveGumrukAgcEntry.pending, (state) => {
        state.isApproving = true;
        state.errorMessage = null;
      })
      .addCase(approveGumrukAgcEntry.fulfilled, (state, action) => {
        state.isApproving = false;
        state.operation = getOperation(action.payload) ?? {
          ...(state.operation ?? {}),
          status: "Approved",
        };
        state.captchaRequired = false;
        state.sessionAvailable = false;
        state.challengeId = null;
        state.challengeExpiresAt = null;
      })
      .addCase(approveGumrukAgcEntry.rejected, (state, action) => {
        state.isApproving = false;
        state.errorMessage = action.payload?.message || "Gümrük giriş bildirimi onaylanamadı.";
      })
      .addCase(rejectGumrukAgcEntry.pending, (state) => {
        state.isRejecting = true;
        state.errorMessage = null;
      })
      .addCase(rejectGumrukAgcEntry.fulfilled, (state, action) => {
        state.isRejecting = false;
        state.operation = getOperation(action.payload) ?? {
          ...(state.operation ?? {}),
          status: "RejectedByUser",
        };
      })
      .addCase(rejectGumrukAgcEntry.rejected, (state, action) => {
        state.isRejecting = false;
        state.errorMessage = action.payload?.message || "Gümrük giriş bildirimi reddedilemedi.";
      })
      .addCase(getGumrukAgcEntryStatus.pending, (state) => {
        state.isCheckingStatus = true;
      })
      .addCase(getGumrukAgcEntryStatus.fulfilled, (state, action) => {
        state.isCheckingStatus = false;
        state.operation = getOperation(action.payload) ?? action.payload ?? state.operation;
        state.operationId = state.operation?.operationId ?? state.operationId;
        state.shipmentId = state.operation?.shipmentId ?? state.shipmentId;
        state.errorMessage = null;
      })
      .addCase(getGumrukAgcEntryStatus.rejected, (state, action) => {
        state.isCheckingStatus = false;
        state.errorMessage = action.payload?.message || "Gümrük işlem durumu alınamadı.";
      })
      .addCase(approveGumrukAgcRegistrationWarning.pending, (state) => {
        state.isApprovingWarning = true;
        state.errorMessage = null;
      })
      .addCase(approveGumrukAgcRegistrationWarning.fulfilled, (state, action) => {
        state.isApprovingWarning = false;
        state.operation = getOperation(action.payload) ?? {
          ...(state.operation ?? {}),
          status: "Approved",
        };
      })
      .addCase(approveGumrukAgcRegistrationWarning.rejected, (state, action) => {
        state.isApprovingWarning = false;
        state.errorMessage = action.payload?.message || "GTB tescil uyarısı onaylanamadı.";
      });
  },
});

export const beginGumrukAgcFlow = ({ operationId, shipmentId, assignmentContext }) => (dispatch) => {
  const pendingOperation = {
    operationId,
    shipmentId: shipmentId ?? null,
    assignmentContext: assignmentContext ?? null,
  };
  try {
    sessionStorage.setItem(GUMRUK_AGC_STORAGE_KEY, JSON.stringify(pendingOperation));
  } catch {
    // Akış Redux state'i üzerinden devam eder; kalıcılaştırma yalnızca yenileme desteğidir.
  }
  dispatch(gumrukAgcSlice.actions.setPendingGumrukAgcOperation(pendingOperation));
};

export const finishGumrukAgcFlow = () => (dispatch) => {
  try {
    sessionStorage.removeItem(GUMRUK_AGC_STORAGE_KEY);
  } catch {
    // Storage erişimi kapalı olsa da bellekteki akış temizlenir.
  }
  dispatch(gumrukAgcSlice.actions.clearGumrukAgcOperation());
};

export const {
  clearGumrukAgcError,
  clearGumrukAgcOperation,
  setGumrukAgcOperationStatus,
  setPendingGumrukAgcOperation,
} = gumrukAgcSlice.actions;

export const getGumrukAgcErrorMessage = (errorOrBody, fallback) =>
  getApiErrorMessage(errorOrBody, fallback);

export default gumrukAgcSlice.reducer;
